import type { DayStatus } from '../types.ts';

/**
 * Fonte única de rótulo, cor e explicação de cada status das barras.
 *
 * As descrições precisam refletir a classificação real feita em
 * backend/src/features/public/public.service.ts (getUptime). Ao mudar a regra lá,
 * mudar o texto aqui.
 */
export interface StatusLegendItem {
  status: DayStatus;
  label: string;
  cssVar: string;
  /** Frase exibida no painel expandido — descreve a classificação CONTRATUAL (anti-flap, pública). */
  descricao: string;
  /**
   * Descrição alternativa para a visão "real" da página interna (barras por amostra bruta). Quando
   * ausente, o painel cai para `descricao`. Mantém o invariante: a legenda reflete a classificação
   * ativa em getUptime (modo raw). Ver [statusLegend em StatusBars].
   */
  descricaoRaw?: string;
}

export const STATUS_LEGEND: StatusLegendItem[] = [
  {
    status: 'up',
    label: 'Operacional',
    cssVar: 'var(--color-status-up)',
    descricao:
      'Nenhuma indisponibilidade confirmada no período. Falhas isoladas e passageiras — que não atingem o limiar do anti-flap — não descontam a disponibilidade e não mudam esta cor.',
    descricaoRaw:
      'Todas as verificações do período obtiveram resposta do servidor.',
  },
  {
    status: 'down',
    label: 'Indisponível',
    cssVar: 'var(--color-status-down)',
    descricao:
      'Nenhuma verificação obteve resposta durante todo o período: tempo esgotado, conexão recusada ou falha de DNS.',
    descricaoRaw:
      'Nenhuma verificação obteve resposta no período: tempo esgotado, conexão recusada ou falha de DNS.',
  },
  {
    status: 'partial',
    label: 'Parcial',
    cssVar: 'var(--color-status-degraded)',
    descricao:
      'Houve indisponibilidade confirmada (falhas consecutivas suficientes para descontar a disponibilidade), mas o sistema também respondeu em parte do período.',
    descricaoRaw:
      'No período houve verificações com resposta e outras sem — inclui oscilações momentâneas que não descontam a disponibilidade.',
  },
  {
    status: 'maintenance',
    label: 'Manutenção',
    cssVar: 'var(--color-status-maintenance)',
    descricao:
      'Havia manutenção programada. Basta a janela tocar o período para ele ser marcado assim, mesmo que tenha durado poucos minutos. Manutenções não entram no cálculo de disponibilidade.',
  },
  {
    status: 'no-data',
    label: 'Sem dados',
    cssVar: '#9ca3af',
    descricao:
      'Não há registro de verificação nesse período — o monitoramento ainda não estava ativo ou esteve pausado.',
  },
];

/** Observações gerais, exibidas junto de qualquer status selecionado. */
export const LEGEND_NOTAS = {
  respostaIncluiErro:
    'Erros HTTP contam como no ar: se o servidor respondeu 404 ou 500, ele estava respondendo. Só é falha quando não há resposta alguma.',
  cliqueNaBarra:
    'Clique em qualquer barra para ver as verificações individuais do período.',
} as const;

export const STATUS_CSS_VAR = Object.fromEntries(
  STATUS_LEGEND.map((i) => [i.status, i.cssVar]),
) as Record<DayStatus, string>;

export const STATUS_LABEL = Object.fromEntries(
  STATUS_LEGEND.map((i) => [i.status, i.label]),
) as Record<DayStatus, string>;
